import React, { useState, useRef, useEffect } from 'react';
import { Upload, File, Send, Bot, User, Loader2, RotateCcw, PlusCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';

const API_BASE_URL = (process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8001').replace('localhost', '127.0.0.1') + '/api';

const CustomRagDemo = () => {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFileNames, setSelectedFileNames] = useState([]);

  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [typingText, setTypingText] = useState('');
  const [activePlaceholder, setActivePlaceholder] = useState(0);

  const placeholders = [
    "Ask anything about your document...",
    "Summarize the key points...",
    "List important dates and deadlines...",
    "Find specific clauses or terms...",
    "Explain this in simple terms..."
  ];

  useEffect(() => {
    // Warm up the backend AI model early to reduce latency on first upload
    const warmupBackend = async () => {
      try {
        await axios.get(`${API_BASE_URL}/warmup`);
        console.log('Backend intelligence engine warmed up');
      } catch (e) {
        console.warn('Warm-up call failed or timed out (expected in some Lambda states)', e);
      }
    };
    warmupBackend();

    const interval = setInterval(() => {
      setActivePlaceholder(prev => (prev + 1) % placeholders.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Only scroll if there's actual conversation or active typing
    if (messages.length > 0 || isTyping) {
      scrollToBottom();
    }
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleStartNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setUploadedFiles([]);
    setSelectedFileNames([]);
    setInputMessage('');
    toast.success('Started a new chat session');
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validFiles = files.filter(f => f.name.endsWith('.pdf'));

    if (validFiles.length !== files.length) {
      toast.error('Only PDF files are allowed');
    }

    // Size check (max 50MB)
    const MAX_SIZE = 50 * 1024 * 1024;
    const oversizedFiles = validFiles.filter(f => f.size > MAX_SIZE);

    if (oversizedFiles.length > 0) {
      toast.error('Some files exceed the 50MB limit and were skipped');
    }

    const acceptedFiles = validFiles.filter(f => f.size <= MAX_SIZE);

    const duplicateFiles = acceptedFiles.filter(f => uploadedFiles.includes(f.name));
    const nonDuplicateFiles = acceptedFiles.filter(f => !uploadedFiles.includes(f.name));

    if (duplicateFiles.length > 0) {
      if (duplicateFiles.length === 1) {
        toast.warning(`${duplicateFiles[0].name} is already present`);
      } else {
        toast.warning(`${duplicateFiles.length} files are already present: ${duplicateFiles.map(f => f.name).join(', ')}`);
      }
    }

    if (nonDuplicateFiles.length > 0) {
      uploadDocuments(nonDuplicateFiles);
    }
  };

  const uploadDocuments = async (files) => {
    setIsUploading(true);

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });

    try {
      const headers = {};
      if (sessionId) {
        headers['X-Session-ID'] = sessionId;
      }

      // Switching to Axios to avoid conflicts with global fetch interceptors like rrweb-recorder
      const response = await axios.post(`${API_BASE_URL}/custom-rag/upload`, formData, {
        headers: headers
      });

      const data = response.data;
      setSessionId(data.session_id);

      const newFileNames = files.map(f => f.name);
      setUploadedFiles(prev => [...prev, ...newFileNames]);

      toast.success('Documents analysed successfully');

      if (messages.length === 0) {
        setMessages([{
          role: 'assistant',
          content: "<strong>Ready!</strong> I've analyzed your documents. What would you like to know?"
        }]);
      }

    } catch (error) {
      console.error('Upload error:', error);
      // Access the detailed error from the backend if available
      const errorMessage = error.response?.data?.detail || error.message || 'An error occurred while uploading. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;
    if (!sessionId) {
      toast.error('Please upload at least one PDF document first.');
      return;
    }

    const newMessage = { role: 'user', content: inputMessage };
    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Filter out only user and assistant messages exactly like ChatMessage model expects
      const history = messages
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map(msg => ({
          role: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.content
        }));

      // Switching to Axios to avoid conflicts with global fetch interceptors like rrweb-recorder
      const response = await axios.post(`${API_BASE_URL}/custom-rag/chat`, {
        session_id: sessionId,
        message: newMessage.content,
        conversation_history: history
      });

      const data = response.data;

      // Add message with typing animation effect toggle
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.message,
        isNew: true
      }]);
      setShowSuggestions(true);

    } catch (error) {
      console.error('Chat error:', error);

      // Handle specific backend errors
      const isApiKeyError = error.response?.status === 403 || error.message?.includes('API key is not configured');
      const errorMessageFromServer = error.response?.data?.detail || '';

      if (errorMessageFromServer.includes('Session not found') || error.response?.status === 404) {
        toast.error('Your session expired. Please upload the documents again.');
        setSessionId(null);
      } else if (isApiKeyError) {
        toast.error('AI Strategy Blocked', {
          description: 'The ThinkVerse backend needs a Gemini API Key to run. Please add GEMINI_API_KEY to your backend/.env file.',
          duration: 8000,
        });
      } else {
        const fallbackMsg = error.response?.data?.detail || error.message || 'Failed to get response. Please try again.';
        toast.error(fallbackMsg);
      }

      const displayMessage = isApiKeyError
        ? '⚠️ <strong>Intelligence Core Offline</strong><br/>The Gemini API key is missing in the backend configuration. Please contact the administrator or add the key to enable AI features.'
        : errorMessageFromServer || 'I encountered an error connecting to the intelligence engine. Please try again.';

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: displayMessage,
        isError: true
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleSuggestionClick = (suggestion) => {
    setInputMessage(suggestion);
    // Auto-submit will be handled by regular form logic if we want, 
    // but for now just fill the input to encourage user action.
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gradient-to-br from-indigo-50/50 via-white to-indigo-50/30 font-sans">
      <Navbar />

      <div className="flex flex-1 pt-20 overflow-hidden h-[calc(100vh-80px)]">
        {/* Sidebar */}
        <div className="w-80 flex-shrink-0 bg-white/80 backdrop-blur-md border-r border-gray-100 flex flex-col h-full shadow-sm">
          <div className="p-6">
            <button
              onClick={handleStartNewChat}
              className="w-full flex items-center justify-center gap-2 bg-indigo-50/50 text-indigo-700 font-semibold py-3 px-4 rounded-2xl hover:bg-indigo-100 transition-all border border-indigo-100/50"
            >
              <PlusCircle size={18} />
              Start New Session
            </button>
          </div>

          <div className="px-6 pb-4 flex-1 overflow-y-auto custom-scrollbar">
            <h3 className="text-[10px] font-bold text-indigo-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
              <Upload size={14} />
              Upload Documents
            </h3>

            <div
              onClick={() => !isUploading && fileInputRef.current?.click()}
              className={`
                border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-all relative overflow-hidden group
                ${isUploading
                  ? 'border-gray-200 bg-gray-50/50 cursor-not-allowed'
                  : 'border-indigo-200 bg-indigo-50/30 hover:bg-white hover:border-indigo-500 hover:shadow-xl hover:shadow-indigo-500/10'}
              `}
            >
              {/* Glow Pulse Background */}
              {!isUploading && (
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-violet-500/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              )}

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf"
                multiple
                className="hidden"
              />
              {isUploading ? (
                <div className="flex flex-col items-center justify-center text-gray-500 gap-3 relative z-10">
                  <div className="relative">
                    <div className="absolute inset-0 bg-indigo-500/20 blur-xl rounded-full animate-pulse"></div>
                    <Loader2 className="w-10 h-10 animate-spin text-indigo-600 relative" />
                  </div>
                  <div className="text-center">
                    <span className="text-sm font-bold text-gray-800 block">Analyzing...</span>
                    <span className="text-[10px] text-gray-400 uppercase tracking-tighter">Preparing your intelligence base</span>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 relative z-10">
                  <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                    <PlusCircle className="text-indigo-600 w-6 h-6" />
                  </div>
                  <div className="text-center">
                    <span className="text-sm font-bold text-gray-800 block group-hover:text-indigo-600 transition-colors">Drop PDF here</span>
                    <span className="text-[10px] text-gray-400 uppercase font-medium">Supports up to 50MB</span>
                  </div>
                </div>
              )}
            </div>

            {uploadedFiles.length > 0 && (
              <div className="mt-8 animate-in fade-in slide-in-from-top-4 duration-500">
                <h3 className="text-[10px] font-bold text-indigo-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                  <File size={14} />
                  Your Knowledge Base
                </h3>
                <div className="space-y-2">
                  {uploadedFiles.map((filename, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-white border border-gray-100 p-3 rounded-xl shadow-sm hover:border-indigo-200 transition-colors group">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center group-hover:bg-indigo-600 transition-colors">
                        <File className="text-indigo-600 w-4 h-4 group-hover:text-white" />
                      </div>
                      <span className="text-xs text-gray-600 truncate font-semibold flex-1">{filename}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col h-full bg-white/40">

          {/* Branded Header */}
          <div className="px-8 py-5 bg-white/70 backdrop-blur-xl border-b border-gray-100 flex justify-between items-center z-20">
            <div>
              <h1 className="text-xl font-black italic tracking-tight text-gray-900 flex items-center gap-2 uppercase">
                <span className="bg-indigo-600 text-white px-2 py-0.5 rounded italic">Insight</span>
                AI
              </h1>
              <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mt-1">Turn Documents into Answers instantly</p>
            </div>
            <div className="hidden lg:flex items-center gap-6 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              <span className="flex items-center gap-1.5"><AlertCircle size={14} className="text-emerald-500" /> 100% Private</span>
              <span className="flex items-center gap-1.5"><Upload size={14} className="text-indigo-500" /> No Signup Required</span>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-12 scroll-smooth">
            <div className="max-w-4xl mx-auto space-y-8">

              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-20 animate-in fade-in zoom-in duration-700">
                  <div className="mb-10 relative">
                    <div className="absolute -inset-10 bg-indigo-500/10 rounded-full blur-3xl animate-pulse"></div>
                    <div className="relative bg-white p-6 rounded-[2.5rem] shadow-2xl shadow-indigo-100 border border-indigo-50">
                      <Bot size={48} className="text-indigo-600" />
                    </div>
                  </div>

                  <h2 className="text-3xl font-black text-gray-900 mb-4 uppercase tracking-tighter">Start Exploring Your Data</h2>
                  <p className="text-gray-500 max-w-lg mb-12 text-lg font-medium leading-relaxed">
                    Upload your PDFs and ask anything. InsightAI finds verified answers directly from your library.
                  </p>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full px-4">
                    {[
                      { step: '01', title: 'Upload', desc: 'Drop PDFs in the sidebar', icon: <Upload className="text-indigo-500" /> },
                      { step: '02', title: 'Ask', desc: 'Type any question below', icon: <Send className="text-violet-500" /> },
                      { step: '03', title: 'Answer', desc: 'Get instant AI insights', icon: <Bot className="text-emerald-500" /> }
                    ].map((item, i) => (
                      <div key={i} className="bg-white/80 backdrop-blur-sm p-8 rounded-[2rem] shadow-sm border border-gray-100 relative group hover:border-indigo-400 hover:shadow-xl hover:shadow-indigo-500/5 transition-all">
                        <div className="absolute -top-3 -right-3 w-10 h-10 bg-white border border-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center font-black text-xs shadow-sm group-hover:bg-indigo-600 group-hover:text-white transition-all">
                          {item.step}
                        </div>
                        <div className="mb-6 flex justify-center scale-110">{item.icon}</div>
                        <h3 className="font-extrabold text-gray-900 mb-2 uppercase tracking-tight">{item.title}</h3>
                        <p className="text-xs text-gray-400 font-medium leading-tight">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-4 duration-500`}
                  >
                    <div className={`flex gap-4 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>

                      {/* Avatar */}
                      <div className={`
                        flex-shrink-0 w-12 h-12 rounded-[1.25rem] flex items-center justify-center shadow-lg transform transition-transform hover:scale-110
                        ${msg.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-100 text-indigo-600'}
                      `}>
                        {msg.role === 'user' ? <User size={24} /> : <Bot size={24} />}
                      </div>

                      {/* Bubble with Actions */}
                      <div className="group relative">
                        <div className={`
                          py-4 px-6 rounded-[2rem] text-sm leading-relaxed
                          ${msg.role === 'user'
                            ? 'bg-indigo-600 text-white rounded-tr-none shadow-xl shadow-indigo-200'
                            : msg.isError
                              ? 'bg-red-50 text-red-800 border border-red-100 rounded-tl-none'
                              : 'bg-white text-gray-800 border border-gray-50 shadow-sm rounded-tl-none'
                          }
                        `}>
                          <div className="prose prose-sm max-w-none prose-p:leading-relaxed" style={{ color: 'inherit' }}>
                            {msg.role === 'assistant' ? (
                              <div dangerouslySetInnerHTML={{
                                __html: msg.content
                                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                  .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                  .replace(/\n\n/g, '<br/><br/>')
                                  .replace(/\n\*/g, '<br/>•')
                                  .replace(/• /g, '• ')
                              }} />
                            ) : (
                              msg.content
                            )}
                          </div>
                        </div>

                        {/* Action Buttons (Visible on hover) */}
                        {msg.role === 'assistant' && !msg.isError && (
                          <div className="absolute -bottom-10 left-0 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => copyToClipboard(msg.content)}
                              className="p-1.5 bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-indigo-600 hover:border-indigo-200 transition-colors shadow-sm"
                              title="Copy Answer"
                            >
                              <PlusCircle size={14} className="rotate-45" />
                            </button>
                            <button
                              className="p-1.5 bg-white border border-gray-100 rounded-lg text-gray-400 hover:text-indigo-600 hover:border-indigo-200 transition-colors shadow-sm"
                              title="Regenerate"
                            >
                              <RotateCcw size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}

              {isTyping && (
                <div className="flex justify-start animate-in fade-in duration-300">
                  <div className="flex gap-4 items-center">
                    <div className="w-12 h-12 rounded-[1.25rem] bg-white border border-gray-100 flex items-center justify-center shadow-lg">
                      <Bot size={24} className="text-indigo-300 animate-pulse" />
                    </div>
                    <div className="flex gap-1.5 bg-white/50 p-4 rounded-full border border-gray-50 shadow-sm">
                      <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Suggestions Chips */}
              {showSuggestions && messages.length > 0 && !isTyping && (
                <div className="flex flex-wrap gap-2 justify-start pl-16 animate-in slide-in-from-left-4 duration-500">
                  {[
                    "List key insights",
                    "Identify action points",
                    "Summarize this answer"
                  ].map((suggestion, i) => (
                    <button
                      key={i}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="px-4 py-2 bg-indigo-50 text-indigo-600 text-xs font-bold rounded-full border border-indigo-100 hover:bg-indigo-600 hover:text-white transition-all transform hover:scale-105"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}

              <div ref={chatEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="p-4 sm:p-8 bg-white/30 backdrop-blur-md border-t border-gray-100 relative z-30">
            <div className="max-w-4xl mx-auto relative group">
              <form
                onSubmit={handleSubmit}
                className="relative flex items-center"
              >
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder={placeholders[activePlaceholder]}
                  className="w-full bg-white border-2 border-transparent focus:border-indigo-500 py-4 pl-6 pr-16 rounded-[2rem] shadow-2xl shadow-indigo-500/10 focus:outline-none transition-all placeholder:text-gray-400 placeholder:italic font-medium"
                />
                <button
                  type="submit"
                  disabled={!inputMessage.trim() || isUploading || isTyping}
                  className="absolute right-2.5 w-12 h-12 bg-indigo-600 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-indigo-700 hover:shadow-indigo-500/40 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-105 active:scale-95 group-focus-within:ring-4 ring-indigo-500/20"
                >
                  <Send size={20} className={isTyping ? "animate-pulse" : ""} />
                </button>
              </form>

              <div className="mt-3 flex justify-between items-center px-6">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest hidden sm:block">
                  InsightAI analyzes in real-time
                </span>
                <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">
                  Try asking: "Give me a quick summary"
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomRagDemo;
